import { useEffect, useRef } from "react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import {
  SURVEY_QUESTIONS,
  SurveyAnswerId,
  agencyLabel,
  isQuestionAnswered,
  otherKey,
  resolveSurveyCopy,
} from "../../common/postTrialSurvey";
import Button from "../common/Button";
import Reveal from "../common/Reveal";
import ChoiceQuestion from "./questions/ChoiceQuestion";
import NpsQuestion from "./questions/NpsQuestion";
import QuoteQuestion from "./questions/QuoteQuestion";
import ScaleQuestion from "./questions/ScaleQuestion";
import { PostTrialSurveyState } from "./usePostTrialSurvey";

// Single-choice, scale and NPS answers advance on their own after a short
// beat so the officer can just tap through; multi and text wait for Next.
const AUTO_ADVANCE_MS = 250;

interface Props {
  survey: PostTrialSurveyState;
}

// The survey is required: there is no close button, backdrop click, or
// Escape. Officers answer each question (a tap each) and finish; only the
// closing quote is optional. While the trial is still running they can
// say "I'm still evaluating" to be asked again a few days later.
function titleFor(survey: PostTrialSurveyState): { title: string; subtitle: string } {
  const agency = agencyLabel(survey.agencyName);
  const title = `${agency === "your department" ? "Your department" : agency} needs your feedback`;
  if (survey.mode === "feedback") {
    return {
      title,
      subtitle: `${agency === "your department" ? "Your department" : agency} is gathering feedback on Police Narratives AI. Command staff will use your answers to decide what happens next. It takes about two minutes.`,
    };
  }
  if (survey.trigger === "post_expiry") {
    return {
      title,
      subtitle: "Your evaluation of Police Narratives AI has ended. Command staff will use your answers to decide what happens next. It takes about two minutes.",
    };
  }
  if (survey.trigger === "manual") {
    return {
      title,
      subtitle: `${agency === "your department" ? "Your department" : agency} is gathering feedback on its evaluation of Police Narratives AI. Command staff will use your answers to decide what happens next. It takes about two minutes.`,
    };
  }
  const days = survey.endsAt
    ? Math.max(1, Math.ceil((survey.endsAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  return {
    title,
    subtitle: `Your evaluation of Police Narratives AI ends ${
      days ? `in ${days} day${days === 1 ? "" : "s"}` : "soon"
    }. Command staff will use your answers to decide what happens next. It takes about two minutes.`,
  };
}

// "on Monday, Oct 5" (the server already capped it at the trial's end).
function postponeLabel(until: Date): string {
  return `on ${until.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}`;
}

const PostTrialSurveyModal: React.FC<Props> = ({ survey }) => {
  const { open, index, answers, quote } = survey;
  const question = SURVEY_QUESTIONS[index];
  const isLast = index === SURVEY_QUESTIONS.length - 1;
  const advanceTimer = useRef<number | null>(null);
  const { title, subtitle } = titleFor(survey);
  const copy = resolveSurveyCopy(question, { mode: survey.mode, agencyName: survey.agencyName });
  const answered = isQuestionAnswered(question, answers, quote);
  // Every question except the closing quote must be answered to move on.
  const canAdvance = answered || question.type === "text";

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Enter moves on unless the officer is typing in the textarea or has
      // an option button focused (Enter should activate that button).
      if (
        e.key === "Enter" &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !(e.target instanceof HTMLButtonElement)
      ) {
        e.preventDefault();
        if (!canAdvance) return;
        if (isLast) void survey.complete();
        else survey.next();
      }
      // Digit keys answer scale questions directly.
      if (question.type === "scale" && /^[1-5]$/.test(e.key)) {
        survey.setAnswer(question.id as SurveyAnswerId, Number(e.key));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, isLast, question, survey, canAdvance]);

  useEffect(
    () => () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    },
    []
  );

  if (!open) return null;

  const autoAdvance = () => {
    if (isLast) return;
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => survey.next(), AUTO_ADVANCE_MS);
  };

  const progressPct = Math.round(((index + 1) / SURVEY_QUESTIONS.length) * 100);

  const renderQuestion = () => {
    switch (question.type) {
      case "scale":
        return (
          <ScaleQuestion
            min={question.min}
            max={question.max}
            minLabel={question.minLabel}
            maxLabel={question.maxLabel}
            value={answers[question.id as SurveyAnswerId] as number | undefined}
            onChange={(v) => {
              survey.setAnswer(question.id as SurveyAnswerId, v);
              autoAdvance();
            }}
          />
        );
      case "nps":
        return (
          <NpsQuestion
            value={answers[question.id as SurveyAnswerId] as number | undefined}
            onChange={(v) => {
              survey.setAnswer(question.id as SurveyAnswerId, v);
              autoAdvance();
            }}
          />
        );
      case "single": {
        const id = question.id as SurveyAnswerId;
        const hasOther = question.options.some((o) => o.allowText);
        return (
          <ChoiceQuestion
            mode="single"
            options={question.options}
            value={answers[id] as string | undefined}
            otherText={answers[otherKey(id)]}
            onOtherTextChange={(t) => survey.setAnswer(otherKey(id), t.trim() ? t : null)}
            onChange={(v) => {
              survey.setAnswer(id, v);
              const picked = question.options.find((o) => o.value === v);
              if (hasOther && !picked?.allowText) survey.setAnswer(otherKey(id), null);
              // An "Other" pick needs its text box, so don't auto-advance.
              if (!picked?.allowText) autoAdvance();
            }}
          />
        );
      }
      case "multi": {
        const id = question.id as SurveyAnswerId;
        const otherValues = question.options.filter((o) => o.allowText).map((o) => o.value);
        return (
          <ChoiceQuestion
            mode="multi"
            options={question.options}
            maxPicks={question.maxPicks}
            value={(answers[id] as string[] | undefined) ?? []}
            otherText={answers[otherKey(id)]}
            onOtherTextChange={(t) => survey.setAnswer(otherKey(id), t.trim() ? t : null)}
            onChange={(v) => {
              survey.setAnswer(id, v.length ? v : null);
              if (otherValues.length && !v.some((x) => otherValues.includes(x))) {
                survey.setAnswer(otherKey(id), null);
              }
            }}
          />
        );
      }
      case "text":
        return (
          <QuoteQuestion
            value={quote}
            maxLength={question.maxLength}
            onChange={survey.setQuote}
            autoFocus
          />
        );
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div
        className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl not-prose"
        role="dialog"
        aria-modal="true"
        aria-labelledby="post-trial-survey-title"
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="post-trial-survey-title" className="text-lg font-bold text-base-content leading-tight">
              {title}
            </h3>
            <p className="text-sm text-base-content/60 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="px-6">
          <div className="flex items-center justify-between text-[11px] text-base-content/50 mb-1.5">
            <span className="tabular-nums">
              {index + 1} of {SURVEY_QUESTIONS.length}
            </span>
            <span className={cn("transition-opacity", survey.saving ? "opacity-100" : "opacity-0")}>
              Saving…
            </span>
          </div>
          <div className="h-1 rounded-full bg-base-300 overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-[width] duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <Reveal key={question.id} className="px-6 pt-5 pb-2">
          <p className="text-base font-semibold text-base-content leading-snug">{copy.prompt}</p>
          {copy.help && <p className="text-xs text-base-content/50 mt-1">{copy.help}</p>}
          <div className="mt-4 max-h-[50vh] overflow-y-auto pr-0.5">{renderQuestion()}</div>
        </Reveal>

        <div className="px-6 pb-6 pt-3 flex items-center justify-between gap-2">
          <Button variant="clean" size="sm" className="px-4" onClick={survey.back} disabled={index === 0 || survey.submitting}>
            Back
          </Button>
          <div className="flex items-center gap-2">
            {!canAdvance && (
              <span className="text-[11px] text-base-content/40">Pick an answer to continue</span>
            )}
            {isLast ? (
              <Button variant="primary" size="sm" className="px-4" onClick={() => void survey.complete()} disabled={survey.submitting}>
                {survey.submitting ? "Sending…" : answered ? "Finish" : "Finish without a quote"}
              </Button>
            ) : (
              <Button variant="primary" size="sm" className="px-4" onClick={survey.next} disabled={!canAdvance || survey.submitting}>
                Next
              </Button>
            )}
          </div>
        </div>
        {survey.postponeUntil && (
          <div className="px-6 pb-5 -mt-2 text-center">
            <button
              type="button"
              onClick={() => void survey.postpone().catch(() => {})}
              disabled={survey.postponing || survey.submitting}
              className="text-xs text-base-content/60 hover:text-blue-600 dark:hover:text-blue-300 hover:underline disabled:opacity-50"
            >
              {survey.postponing
                ? "Saving…"
                : `I'm still evaluating, ask me again ${postponeLabel(survey.postponeUntil)}`}
            </button>
          </div>
        )}
      </div>
      {/* No backdrop button: the survey is required and can't be clicked away. */}
      <div className="modal-backdrop" aria-hidden />
    </dialog>
  );
};

export default PostTrialSurveyModal;
